import { Controller } from "@hotwired/stimulus"

// Phase 2.8 Tier 3 — Schedule form controller. Renders dynamic
// channel and recipient rows, then serializes both lists into hidden
// JSON payloads on submit so the server controller stays Rails-y
// (`channels_payload` / `recipients_payload` strong-params keys).
//
// Channel kinds:
//   email   — uses recipient list (separate block)
//   webhook — needs a target URL + format (json default)
//   s3      — needs an s3:// path + format (xlsx default)
export default class extends Controller {
  static targets = ["channelsList", "recipientsList", "recipientsBlock",
                    "channelsPayload", "recipientsPayload",
                    "frequency", "weeklyField", "monthlyField"]
  static values = {
    channels: { type: Array, default: [] },
    recipients: { type: Array, default: [] },
    companyUsers: { type: Array, default: [] }
  }

  connect() {
    this.channels = JSON.parse(JSON.stringify(this.channelsValue || []))
    this.recipients = JSON.parse(JSON.stringify(this.recipientsValue || []))
    if (this.channels.length === 0) {
      this.channels = [{ kind: "email", format: "pdf", target: "" }]
    }
    this.renderChannels()
    this.renderRecipients()
    this.toggleRecipientsBlock()
    this.element.addEventListener("submit", () => this.serialize())
  }

  // ── Frequency picker ─────────────────────────────────────────────

  frequencyChanged() {
    const f = this.frequencyTarget.value
    this.weeklyFieldTarget.classList.toggle("hidden", f !== "weekly")
    this.monthlyFieldTarget.classList.toggle("hidden", !["monthly", "quarterly"].includes(f))
  }

  // ── Channels ─────────────────────────────────────────────────────

  addChannel(event) {
    event.preventDefault()
    this.channels.push({ kind: "email", format: "pdf", target: "" })
    this.renderChannels()
    this.toggleRecipientsBlock()
  }

  removeChannel(event) {
    event.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    this.channels.splice(idx, 1)
    this.renderChannels()
    this.toggleRecipientsBlock()
  }

  channelKindChanged(event) {
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    const next = event.currentTarget.value
    this.channels[idx].kind = next
    if (next === "email") this.channels[idx].format = "pdf"
    if (next === "webhook") this.channels[idx].format = "json"
    if (next === "s3") this.channels[idx].format = "xlsx"
    this.renderChannels()
    this.toggleRecipientsBlock()
  }

  channelFieldChanged(event) {
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    const field = event.currentTarget.dataset.field
    this.channels[idx][field] = event.currentTarget.value
  }

  renderChannels() {
    this.channelsListTarget.innerHTML = ""
    this.channels.forEach((channel, idx) => {
      const row = document.createElement("div")
      row.className = "bg-white border border-gray-200 rounded p-2 flex items-start gap-2"
      row.innerHTML = `
        <select data-action="change->report-schedule#channelKindChanged"
                data-index="${idx}"
                class="w-28 px-2 py-1 text-[13px] border border-gray-200 rounded bg-white cursor-pointer">
          <option value="email" ${channel.kind === "email" ? "selected" : ""}>Email</option>
          <option value="webhook" ${channel.kind === "webhook" ? "selected" : ""}>Webhook</option>
          <option value="s3" ${channel.kind === "s3" ? "selected" : ""}>S3</option>
        </select>
        <select data-action="change->report-schedule#channelFieldChanged"
                data-index="${idx}" data-field="format"
                class="w-24 px-2 py-1 text-[13px] border border-gray-200 rounded bg-white cursor-pointer">
          ${this._formatOptions(channel.kind, channel.format)}
        </select>
        ${this._targetInput(channel, idx)}
        <button type="button"
                data-action="click->report-schedule#removeChannel"
                data-index="${idx}"
                class="text-[12px] px-2 py-1 text-rose-700 hover:bg-rose-50 rounded">Remove</button>
      `
      this.channelsListTarget.appendChild(row)
    })
  }

  _formatOptions(kind, current) {
    const formats = {
      email: ["pdf", "xlsx", "csv"],
      webhook: ["json"],
      s3: ["pdf", "xlsx", "csv", "json"]
    }
    return (formats[kind] || ["pdf"])
      .map(f => `<option value="${f}" ${f === current ? "selected" : ""}>${f.toUpperCase()}</option>`)
      .join("")
  }

  _targetInput(channel, idx) {
    if (channel.kind === "email") {
      return `<div class="flex-1 text-[12px] text-gray-500 px-2 py-1.5">Recipients picked below.</div>`
    }
    const placeholder = channel.kind === "webhook"
      ? "https://your-endpoint/conslog-hook"
      : "s3://bucket/path/"
    return `<input type="text" placeholder="${placeholder}"
              value="${(channel.target || "").replace(/"/g, "&quot;")}"
              data-action="input->report-schedule#channelFieldChanged"
              data-index="${idx}" data-field="target"
              class="flex-1 px-2 py-1 text-[13px] border border-gray-200 rounded bg-white">`
  }

  // ── Recipients ───────────────────────────────────────────────────

  toggleRecipientsBlock() {
    const hasEmail = this.channels.some(c => c.kind === "email")
    this.recipientsBlockTarget.classList.toggle("hidden", !hasEmail)
  }

  addRecipient(event) {
    event.preventDefault()
    this.recipients.push({ email: "", name: "" })
    this.renderRecipients()
  }

  removeRecipient(event) {
    event.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    this.recipients.splice(idx, 1)
    this.renderRecipients()
  }

  recipientFieldChanged(event) {
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    const field = event.currentTarget.dataset.field
    this.recipients[idx][field] = event.currentTarget.value
  }

  recipientUserPicked(event) {
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    const userId = parseInt(event.currentTarget.value, 10)
    const user = (this.companyUsersValue || []).find(u => u.id === userId)
    if (user) {
      this.recipients[idx].user_id = user.id
      this.recipients[idx].email = user.email
      this.recipients[idx].name = user.name
      this.renderRecipients()
    }
  }

  renderRecipients() {
    this.recipientsListTarget.innerHTML = ""
    this.recipients.forEach((r, idx) => {
      const row = document.createElement("div")
      row.className = "bg-white border border-gray-200 rounded p-2 flex items-center gap-2"
      const userPicker = (this.companyUsersValue || []).length
        ? `<select data-action="change->report-schedule#recipientUserPicked"
                   data-index="${idx}"
                   class="w-44 px-2 py-1 text-[13px] border border-gray-200 rounded bg-white cursor-pointer">
             <option value="">— company user —</option>
             ${(this.companyUsersValue || []).map(u => `<option value="${u.id}" ${u.id === r.user_id ? "selected" : ""}>${u.name || u.email}</option>`).join("")}
           </select>`
        : ""
      row.innerHTML = `
        ${userPicker}
        <input type="email" placeholder="email@example.com"
               value="${(r.email || "").replace(/"/g, "&quot;")}"
               data-action="input->report-schedule#recipientFieldChanged"
               data-index="${idx}" data-field="email"
               class="flex-1 px-2 py-1 text-[13px] border border-gray-200 rounded bg-white">
        <input type="text" placeholder="Name (optional)"
               value="${(r.name || "").replace(/"/g, "&quot;")}"
               data-action="input->report-schedule#recipientFieldChanged"
               data-index="${idx}" data-field="name"
               class="w-44 px-2 py-1 text-[13px] border border-gray-200 rounded bg-white">
        <button type="button"
                data-action="click->report-schedule#removeRecipient"
                data-index="${idx}"
                class="text-[12px] px-2 py-1 text-rose-700 hover:bg-rose-50 rounded">Remove</button>
      `
      this.recipientsListTarget.appendChild(row)
    })
  }

  // ── Serialization ────────────────────────────────────────────────

  serialize() {
    this.channelsPayloadTarget.value = JSON.stringify(this.channels)
    this.recipientsPayloadTarget.value = JSON.stringify(this.recipients)
  }
}
